import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import { fetch_needs_attention_daily } from '../api';
import { fmt_count, month_label } from '../format';
import { ist_day } from '../shared/ist';
import { DAILY_LOAD_FAILED_TEXT, daily_view } from './adapters';
import {
  MAX_DAILY_LOOKBACK_DAYS,
  accept_day,
  can_shift_day,
  daily_params,
  shift_day
} from './params';

/*
 * PROD-7828 R5b — the DAILY report: what is new or cleared since the previous digest post, plus the
 * anomaly threshold crossings and the worker outages of that window. The window is [day-1 10:00 IST,
 * day 10:00 IST) — the digest cadence, so the Slack post and this page describe the same 24 hours.
 * The MONTHLY report (the running state of each open month) is the other view of this tab.
 */
const line_columns = [
  {
    key: 'label',
    label: 'Issue',
    render: (l) => (
      <div>
        <div>
          <StatusPill tone={l.tier_tone}>{l.tier_label}</StatusPill> {l.label}
        </div>
        <div className="tp-card-field-label tp-mono">{l.issue_code}</div>
      </div>
    )
  },
  { key: 'count', label: 'Issues', align: 'right', render: (l) => fmt_count(l.count) },
  { key: 'teachers', label: 'Teachers', align: 'right', render: (l) => fmt_count(l.teachers) },
  {
    key: 'amount_at_stake',
    label: '₹ at stake',
    align: 'right',
    render: (l) => <span className="tp-strong">{l.amount_text}</span>
  },
  { key: 'months_text', label: 'Months' }
];

const system_now = () => new Date();

// can_open_month: whether the shell lists the link's month; an unlisted month gets no button.
const DailyReport = ({
  day,
  now = system_now,
  on_day,
  on_error,
  can_open_month,
  on_open_monthly
}) => {
  const today = ist_day(now());
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => daily_view(null, today));
  const [load_failed, setLoadFailed] = useState(false);
  const request_seq = useRef(0);

  const load = useCallback(
    (next_day) => {
      request_seq.current += 1;
      const seq = request_seq.current;
      setLoading(true);
      setLoadFailed(false);
      fetch_needs_attention_daily(daily_params(next_day))
        .then((data) => {
          if (seq !== request_seq.current) return;
          setView(daily_view(data, today));
          setLoading(false);
        })
        .catch((e) => {
          if (seq !== request_seq.current) return;
          on_error(e, 'Failed to load the daily report.');
          setView(daily_view(null, today));
          setLoadFailed(true);
          setLoading(false);
        });
    },
    [on_error, today]
  );

  useEffect(() => {
    load(day);
  }, [day, load]);

  // A day outside the served window (future, or older than 90 days) is never requested — the backend
  // would answer BAD_DAY and Ops would see an error instead of an empty day.
  const pick_day = (next) => {
    if (!accept_day(next, today)) return;
    on_day(next);
  };

  // After a failed load the view is empty; the picker stays on the day that was asked for.
  const shown_day = view.day || day || today;
  const prev_off = !can_shift_day(shown_day, -1, today);
  const next_off = !can_shift_day(shown_day, 1, today);

  return (
    <div>
      <div className="tp-frow">
        {/* aria-disabled, not disabled: an arrow that reaches the edge keeps focus (CD-114). */}
        <button
          type="button"
          className="tp-btn-ghost"
          onClick={prev_off ? undefined : () => pick_day(shift_day(shown_day, -1))}
          aria-disabled={prev_off ? 'true' : undefined}
        >
          ← Previous day
        </button>
        <input
          className="tp-input"
          type="date"
          value={shown_day}
          max={today}
          min={shift_day(today, -MAX_DAILY_LOOKBACK_DAYS)}
          onChange={(e) => pick_day(e.target.value)}
          aria-label="Report day (IST)"
        />
        <button
          type="button"
          className="tp-btn-ghost"
          onClick={next_off ? undefined : () => pick_day(shift_day(shown_day, 1))}
          aria-disabled={next_off ? 'true' : undefined}
        >
          Next day →
        </button>
        <div className="tp-frow-right">
          {view.monthly_target.pay_month && can_open_month(view.monthly_target.pay_month) ? (
            <button
              type="button"
              className="tp-link"
              onClick={() => on_open_monthly(view.monthly_target)}
            >
              Open the {month_label(view.monthly_target.pay_month)} monthly report
            </button>
          ) : null}
        </div>
      </div>

      {loading ? <CustomLoading /> : null}
      {!loading && load_failed ? (
        <LoadFailed text={DAILY_LOAD_FAILED_TEXT} on_retry={() => load(day)} />
      ) : null}
      {!loading && !load_failed ? (
        <div>
          <div className={`tp-banner ${view.all_clear ? 'tp-banner-ok' : 'tp-banner-info'}`}>
            <div className="tp-na-headline">
              {view.all_clear ? 'All clear — nothing moved in this window.' : view.totals_text}
            </div>
            <div>{view.window_text}</div>
          </div>

          {view.sections.map((s) => (
            <div className="tp-card" key={s.key}>
              <div className="tp-card-hd">
                <span className="tp-card-hd-title">{s.title}</span>
              </div>
              <DataTable
                columns={line_columns}
                rows={s.lines}
                rowKey={(l) => l.key}
                emptyText={s.empty_text}
                cardTitle={(l) => l.label}
              />
            </div>
          ))}

          <div className="tp-card">
            <div className="tp-card-hd">
              <span className="tp-card-hd-title">Jobs</span>
            </div>
            {view.job_events.length ? (
              view.job_events.map((j) => (
                <div key={j.key} className="tp-na-blocker">
                  <StatusPill tone={j.tone}>{j.event_label}</StatusPill> {j.text} · {j.at}
                </div>
              ))
            ) : (
              <div className="tp-empty">No worker outage or recovery in this window.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DailyReport;
